import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { message } from 'antd';

import { LoginCustomError } from 'types';
import { ACCOUNT_INFO } from 'events/types';
import { getAccountData } from 'services/accounts';

import LoginForm from '../components/forms/login-form';
import HeaderSecondary from '../components/Header/headerSecondary';
import { useAuth } from '../providers/user-context';
import { activateUser, loginUser } from '../services/auth';
import { toastError } from '../services/helpers/getError';
import useTranslation from '../customHooks/translate';

const Login = () => {
  const { authenticate } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const translation = useTranslation();

  const [loginError, setLoginError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);

  const { refetch: refetchAccountInfo } = useQuery({
    queryKey: [ACCOUNT_INFO],
    queryFn: getAccountData,
    enabled: false,
    select: (response) => response.data.data,
  });

  const { mutate: activateHandler } = useMutation(activateUser, {
    onSuccess() {
      message.success(`${translation.messages.successActive}`);
      navigate('/login', { replace: true });
    },
    onError(err) {
      toastError(err);
      navigate('/check-email', { replace: true });
    },
  });

  const { mutate: loginHandler, isLoading } = useMutation(loginUser, {
    async onSuccess({ data }) {
      authenticate(data);

      const result = await refetchAccountInfo();

      const user = result.data;

      if (!user?.legalDocNumber) {
        navigate('/profile/details', { replace: true });
      } else {
        navigate('/dashboard', { replace: true });
      }

      message.success(translation.messages.successSign);
    },
    onError(error: LoginCustomError) {
      const errorMessage = error.response?.data?.Message;
      const formattedMessage = errorMessage
        ? errorMessage.charAt(0).toUpperCase() + errorMessage.slice(1)
        : null;

      errorMessage?.length && message.error(formattedMessage);
      setLoginError(error.response?.data?.errors?.Login?.[0] || null);
      setPasswordError(error.response?.data?.errors?.Password?.[0] || null);
    },
  });

  useEffect(() => {
    const userInfo = JSON.parse(localStorage.getItem('user') || '{}');

    if (userInfo) {
      authenticate(userInfo);
      navigate('/dashboard', { replace: true });
    }
  }, [authenticate, navigate]);

  useEffect(() => {
    if (!!location.search.length) {
      const param = location.search.substring(1);
      const token = param.replace('activation=', '');
      activateHandler(token);
    }
  }, [activateHandler, location.search]);

  return (
    <section className="box-border grid h-screen grid-cols-1 gap-[60px] overflow-hidden px-[24px] py-[24px]  md:pr-[24px] lg:grid-cols-2 xl:grid-cols-3 xl:gap-[120px] xl:pl-[156px]">
      <div className="col-span-1 flex flex-col gap-[96px]">
        <HeaderSecondary />
        <div className="flex flex-col gap-[24px]">
          <h1 className="text-4xl font-bold text-[#222]">
            {translation.login.SignIn}
          </h1>
          <LoginForm
            loginHandler={loginHandler}
            isLoading={isLoading}
            loginError={loginError}
            passwordError={passwordError}
          />
        </div>
      </div>

      <div className="cols-span-1 hidden h-full overflow-hidden rounded-[24px] lg:block xl:col-span-2">
        <img
          src="assets/login-cover.svg"
          alt="login-cover"
          className="h-full w-full object-cover"
        />
      </div>
    </section>
  );
};

export default Login;
